import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { BiasTaskId } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-bias-task';
import { BiasTaskIdToTaskRecord } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-bias-task';
import { BiasTaskWithoutId } from 'libs/end-to-end/src/types/databases/metrics/metric-definition-bias-task';
import { MetricDefinition } from 'libs/end-to-end/src/types/databases/metrics/metric-definition';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { TransformUniqueValuesByColumnItem } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { okResult } from 'libs/end-to-end/src/result';
import { Result } from 'libs/end-to-end/src/result';
import { UnknownError } from 'libs/end-to-end/src/types/errors/unknown-error';
import { checkIsMetricBoolean } from 'libs/end-to-end/src/utils/databases/check-is-metric-boolean';

export const defaultBiasTasksForFocusArea = ({
  focusArea,
  metric,
  performanceMetric,
  uniqueColumnValues,
}: {
  focusArea: FocusArea;
  metric: MetricDefinition;
  performanceMetric: MetricDefinition | null;
  uniqueColumnValues: TransformUniqueValuesByColumnItem[];
}): Result<BiasTaskIdToTaskRecord, UnknownError> => {
  const isMetricBooleanResult = checkIsMetricBoolean(
    metric.columnHeaderValue,
    uniqueColumnValues,
  );

  if (isMetricBooleanResult.resultType === 'err') {
    return isMetricBooleanResult;
  }

  const isMetricBoolean = isMetricBooleanResult.value;

  let result: Record<BiasTaskId, BiasTaskWithoutId | null>;
  if (focusArea === 'Attrition') {
    result = {
      calculate_skill_index: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: null,
        isTwoSided: false,
      },
      calculate_disparities: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: isMetricBoolean ? null : 'Log',
        isTwoSided: false,
      },
      calculate_department_disparities: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: isMetricBoolean ? null : 'Log',
        isTwoSided: false,
      },
      altonji_pierret_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: null,
        isTwoSided: false,
      },
      tech_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: null,
        isTwoSided: false,
      },
      fryer_jackson_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: null,
        isTwoSided: false,
      },
      department_disparity_pref_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: isMetricBoolean ? null : 'Log',
        isTwoSided: false,
      },
      anwar_fang_pref_bias_test: null,
      becker_pref_bias_test: null,
      cornell_welch_info_bias_test: null,
      focus_area_predictor: null,
      fryer_trajectory_pref_bias_test: null,
      hiring_manager_info_bias_test: null,
      referral_info_bias_test: null,
      referral_struct_bias_test: null,
      same_manager_trajectory_info_bias_test_entry: null,
      same_manager_trajectory_info_bias_test_growth: null,
      threshold_analysis: null,
      expected_performance_calculation: null,
    };
  } else if (focusArea === 'Engagement') {
    result = {
      calculate_skill_index: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      calculate_disparities: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'LogAverage',
        isTwoSided: false,
      },
      calculate_department_disparities: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      altonji_pierret_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      tech_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      fryer_jackson_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      same_manager_trajectory_info_bias_test_entry: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Joining',
        isTwoSided: false,
      },
      same_manager_trajectory_info_bias_test_growth: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Growth',
        isTwoSided: false,
      },
      referral_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Joining',
        isTwoSided: false,
      },
      fryer_trajectory_pref_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Variance',
        isTwoSided: false,
      },
      department_disparity_pref_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      anwar_fang_pref_bias_test: null,
      becker_pref_bias_test: null,
      cornell_welch_info_bias_test: null,
      hiring_manager_info_bias_test: null,
      referral_struct_bias_test: null,
      focus_area_predictor: null,
      threshold_analysis: null,
      expected_performance_calculation: null,
    };
  } else if (focusArea === 'Performance') {
    result = {
      calculate_skill_index: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      calculate_disparities: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'LogAverage',
        isTwoSided: false,
      },
      calculate_department_disparities: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      altonji_pierret_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      tech_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      fryer_jackson_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      same_manager_trajectory_info_bias_test_entry: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Joining',
        isTwoSided: false,
      },
      same_manager_trajectory_info_bias_test_growth: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Growth',
        isTwoSided: false,
      },
      referral_info_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Joining',
        isTwoSided: false,
      },
      fryer_trajectory_pref_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Variance',
        isTwoSided: false,
      },
      department_disparity_pref_bias_test: {
        taskMetric: metric.columnHeaderValue,
        taskMetricDisplayName: metric.displayName,
        taskMetricModifier: 'Average',
        isTwoSided: false,
      },
      anwar_fang_pref_bias_test: null,
      becker_pref_bias_test: null,
      cornell_welch_info_bias_test: null,
      hiring_manager_info_bias_test: null,
      referral_struct_bias_test: null,
      focus_area_predictor: null,
      threshold_analysis: null,
      expected_performance_calculation: null,
    };
  } else if (focusArea === 'Leadership') {
    result = {
      calculate_skill_index: null,
      calculate_disparities: null,
      calculate_department_disparities: null,
      altonji_pierret_info_bias_test: null,
      tech_info_bias_test: null,
      fryer_jackson_info_bias_test: null,
      same_manager_trajectory_info_bias_test_entry: null,
      same_manager_trajectory_info_bias_test_growth: null,
      referral_info_bias_test: null,
      fryer_trajectory_pref_bias_test: null,
      anwar_fang_pref_bias_test: null,
      becker_pref_bias_test: null,
      cornell_welch_info_bias_test: null,
      department_disparity_pref_bias_test: null,
      hiring_manager_info_bias_test: null,
      referral_struct_bias_test: null,
      focus_area_predictor: null,
      threshold_analysis: null,
      expected_performance_calculation: null,
    };
  } else if (focusArea === 'Hiring') {
    const beckerPrefBiasTestGate = metric.isPreferred;
    const anwarFangPrefBiasTestGate = metric.isPreferred;
    const cornellWelchInfoBiasTestGate = metric.isPreferred;
    if (performanceMetric) {
      result = {
        calculate_skill_index: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        calculate_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: isMetricBoolean ? null : 'Log',
          isTwoSided: false,
        },
        calculate_department_disparities: null,
        becker_pref_bias_test: beckerPrefBiasTestGate
          ? {
              taskMetric: performanceMetric.columnHeaderValue,
              taskMetricDisplayName: performanceMetric.displayName,
              taskMetricModifier: 'Joining',
              isTwoSided: false,
            }
          : null,
        anwar_fang_pref_bias_test: anwarFangPrefBiasTestGate
          ? {
              taskMetric: performanceMetric.columnHeaderValue,
              taskMetricDisplayName: performanceMetric.displayName,
              taskMetricModifier: 'Joining',
              isTwoSided: false,
            }
          : null,
        tech_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        fryer_jackson_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        cornell_welch_info_bias_test: cornellWelchInfoBiasTestGate
          ? {
              taskMetric: performanceMetric.columnHeaderValue,
              taskMetricDisplayName: performanceMetric.displayName,
              taskMetricModifier: 'Joining',
              isTwoSided: false,
            }
          : null,
        threshold_analysis: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        expected_performance_calculation: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        referral_struct_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        altonji_pierret_info_bias_test: null,
        department_disparity_pref_bias_test: null,
        focus_area_predictor: null,
        fryer_trajectory_pref_bias_test: null,
        hiring_manager_info_bias_test: null,
        referral_info_bias_test: null,
        same_manager_trajectory_info_bias_test_entry: null,
        same_manager_trajectory_info_bias_test_growth: null,
      };
    } else {
      result = {
        calculate_skill_index: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        calculate_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: isMetricBoolean ? null : 'Log',
          isTwoSided: false,
        },
        calculate_department_disparities: null,
        tech_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        fryer_jackson_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        threshold_analysis: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        expected_performance_calculation: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        referral_struct_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        becker_pref_bias_test: null,
        anwar_fang_pref_bias_test: null,
        cornell_welch_info_bias_test: null,
        department_disparity_pref_bias_test: null,
        focus_area_predictor: null,
        hiring_manager_info_bias_test: null,
        referral_info_bias_test: null,
        same_manager_trajectory_info_bias_test_entry: null,
        same_manager_trajectory_info_bias_test_growth: null,
        altonji_pierret_info_bias_test: null,
        fryer_trajectory_pref_bias_test: null,
      };
    }
  } else if (focusArea === 'Promotion') {
    const beckerPrefBiasTestGate = isMetricBoolean;
    if (performanceMetric) {
      result = {
        calculate_skill_index: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        calculate_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: isMetricBoolean ? null : 'Log',
          isTwoSided: false,
        },
        calculate_department_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: isMetricBoolean ? null : 'Log',
          isTwoSided: false,
        },
        becker_pref_bias_test: beckerPrefBiasTestGate
          ? {
              taskMetric: performanceMetric.columnHeaderValue,
              taskMetricDisplayName: performanceMetric.displayName,
              taskMetricModifier: 'Leaving',
              isTwoSided: false,
            }
          : null,
        altonji_pierret_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        tech_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        fryer_jackson_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        same_manager_trajectory_info_bias_test_entry: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        same_manager_trajectory_info_bias_test_growth: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        referral_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        threshold_analysis: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        expected_performance_calculation: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        department_disparity_pref_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        anwar_fang_pref_bias_test: null,
        cornell_welch_info_bias_test: null,
        focus_area_predictor: null,
        fryer_trajectory_pref_bias_test: null,
        hiring_manager_info_bias_test: null,
        referral_struct_bias_test: null,
      };
    } else {
      result = {
        calculate_skill_index: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        calculate_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: isMetricBoolean ? null : 'Log',
          isTwoSided: false,
        },
        calculate_department_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: isMetricBoolean ? null : 'Log',
          isTwoSided: false,
        },
        altonji_pierret_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        tech_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        fryer_jackson_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        same_manager_trajectory_info_bias_test_entry: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        same_manager_trajectory_info_bias_test_growth: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        referral_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        threshold_analysis: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        expected_performance_calculation: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        department_disparity_pref_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: null,
          isTwoSided: false,
        },
        anwar_fang_pref_bias_test: null,
        cornell_welch_info_bias_test: null,
        focus_area_predictor: null,
        fryer_trajectory_pref_bias_test: null,
        hiring_manager_info_bias_test: null,
        referral_struct_bias_test: null,
        becker_pref_bias_test: null,
      };
    }
  } else if (focusArea === 'Compensation') {
    if (performanceMetric) {
      result = {
        calculate_skill_index: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        calculate_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Log',
          isTwoSided: false,
        },
        calculate_department_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Log',
          isTwoSided: false,
        },
        becker_pref_bias_test: {
          taskMetric: performanceMetric.columnHeaderValue,
          taskMetricDisplayName: performanceMetric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        anwar_fang_pref_bias_test: {
          taskMetric: performanceMetric.columnHeaderValue,
          taskMetricDisplayName: performanceMetric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        altonji_pierret_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Leaving',
          isTwoSided: false,
        },
        tech_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        fryer_jackson_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        same_manager_trajectory_info_bias_test_entry: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        same_manager_trajectory_info_bias_test_growth: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Growth',
          isTwoSided: false,
        },
        referral_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        hiring_manager_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        department_disparity_pref_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        expected_performance_calculation: null,
        threshold_analysis: null,
        cornell_welch_info_bias_test: null,
        fryer_trajectory_pref_bias_test: null,
        referral_struct_bias_test: null,
        focus_area_predictor: null,
      };
    } else {
      result = {
        calculate_skill_index: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        calculate_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Log',
          isTwoSided: false,
        },
        calculate_department_disparities: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Log',
          isTwoSided: false,
        },
        altonji_pierret_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Leaving',
          isTwoSided: false,
        },
        tech_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        fryer_jackson_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        same_manager_trajectory_info_bias_test_entry: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        same_manager_trajectory_info_bias_test_growth: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Growth',
          isTwoSided: false,
        },
        referral_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        hiring_manager_info_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        department_disparity_pref_bias_test: {
          taskMetric: metric.columnHeaderValue,
          taskMetricDisplayName: metric.displayName,
          taskMetricModifier: 'Joining',
          isTwoSided: false,
        },
        expected_performance_calculation: null,
        threshold_analysis: null,
        becker_pref_bias_test: null,
        referral_struct_bias_test: null,
        anwar_fang_pref_bias_test: null,
        cornell_welch_info_bias_test: null,
        fryer_trajectory_pref_bias_test: null,
        focus_area_predictor: null,
      };
    }
  } else {
    throw guardExhaustive(focusArea);
  }

  return okResult(result);
};
